// Generated from scripts/wishlist.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { isProductId, isProductIdItem } from '@theme/product-id';
import { createStoredList } from '@theme/stored-list';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';
/*
 * The wishlist: the products a shopper saved on this device, the heart that saves them, and the count that shows
 * how many there are.
 *
 * - The list is `{ id, handle, variantId? }` entries in the order they were saved, at most 50, kept in the
 *   browser's localStorage under `wontio-wishlist` by the stored list owner (`stored-list.ts`, which also keeps the
 *   list in memory when storage is blocked or full). Nothing else is stored: no title, price or image, since
 *   Shopify renders those fresh for the shopper's market, and no customer data. A list that follows a customer
 *   across devices needs an app that writes customer metafields; the theme does not pretend to be one.
 *   The variant id is stored when the shopper saved from a product page with a selection, so a quick add can
 *   start from it.
 * - Every change goes through `addToWishlist`, `removeFromWishlist` and `mergeIntoWishlist`, which publish a
 *   `wishlist:change` event on `document` with `{ reason, items, item?, added?, refused? }` in its detail. The
 *   reasons are `add`, `remove`, `merge` (a shared list was saved into this one), `full` (a product was refused
 *   because the list holds 50) and `sync` (the list changed without an outcome to announce: another tab, a page
 *   restored from the back-forward cache, or a product that no longer exists). The hearts, the counts and the
 *   wishlist page follow that event; none reads another's DOM.
 * - `<wontio-wishlist-toggle>` is the heart (`snippets/wishlist-button.liquid`). It follows the list and changes
 *   it, names the outcome once in its own polite region, and, on a product page, remembers the variant the shopper
 *   has selected. It extends `Component` so a card that a swatch change morphs gets its state again.
 * - `<wontio-wishlist-count>` is the count on the header's wishlist link and the bottom bar's wishlist item, in the
 *   cart's own bubble.
 *
 * Messages that do not depend on the product come from `Theme.translations` (`snippets/scripts.liquid`), printed
 * once per page in the shopper's language, so a card carries only its own product's text.
 */
export const WISHLIST_LIMIT = 50;
const STORAGE_KEY = 'wontio-wishlist';
/** How long a toggle's polite region keeps a message, so a screen reader never meets a stale sentence. */
const ANNOUNCEMENT_MS = 6000;
/** A list entry from untrusted input (storage, a toggle's attributes), or undefined when it cannot be one. */
function toItem(value) {
    if (!isProductIdItem(value))
        return undefined;
    const variantId = 'variantId' in value ? value.variantId : undefined;
    return isProductId(variantId) ? { id: value.id, handle: value.handle, variantId } : { id: value.id, handle: value.handle };
}
const stored = createStoredList({ storage: 'local', key: STORAGE_KEY, limit: WISHLIST_LIMIT, parseEntry: toItem });
export class WishlistChangeEvent extends CustomEvent {
    static eventName = 'wishlist:change';
    constructor(detail) {
        super(WishlistChangeEvent.eventName, { detail });
    }
}
function publish(change) {
    document.dispatchEvent(new WishlistChangeEvent(change));
}
/** The saved products, in the order they were saved. */
export function readWishlist() {
    return stored.read();
}
export function isSaved(id) {
    return stored.read().some((item) => item.id === id);
}
/** Saves a product at the end of the list. A product the list holds stays where it is; a 51st product is refused and published as `full`. */
export function addToWishlist(item) {
    const current = stored.read();
    if (current.some(({ id }) => id === item.id))
        return 'present';
    if (current.length >= WISHLIST_LIMIT) {
        publish({ reason: 'full', items: current, item });
        return 'full';
    }
    const items = stored.write([...current, item]);
    publish({ reason: 'add', items, item });
    return 'added';
}
/** Takes a product out of the list. `sync` is for a product that no longer exists, which needs no announcement. */
export function removeFromWishlist(id, reason = 'remove') {
    const current = stored.read();
    const item = current.find((entry) => entry.id === id);
    if (!item)
        return false;
    const items = stored.write(current.filter((entry) => entry.id !== id));
    publish({ reason, items, item });
    return true;
}
/**
 * Saves the products of a shared list that this list does not hold, after the ones it holds, until it is full. The
 * products it holds keep their place and their variant, so saving a shared list never overwrites the shopper's own.
 */
export function mergeIntoWishlist(incoming) {
    const current = stored.read();
    const known = new Set(current.map(({ id }) => id));
    const fresh = [];
    for (const { id, handle } of incoming) {
        if (known.has(id))
            continue;
        known.add(id);
        fresh.push({ id, handle });
    }
    const accepted = fresh.slice(0, Math.max(0, WISHLIST_LIMIT - current.length));
    const refused = fresh.length - accepted.length;
    if (accepted.length > 0) {
        const items = stored.write([...current, ...accepted]);
        publish({ reason: 'merge', items, added: accepted.length, refused });
    }
    return { added: accepted.length, refused };
}
// Another tab saved or removed a product, or a page restored from the back-forward cache missed changes.
stored.subscribe(() => publish({ reason: 'sync', items: stored.read() }));
/** A message of `Theme.translations`, with `[limit]` filled in. The page prints them in the shopper's language. */
export function wishlistMessage(key) {
    return (Theme.translations[key] ?? '').replace('[limit]', String(WISHLIST_LIMIT));
}
function isProductSelect(event) {
    return 'promise' in event && event.promise instanceof Promise;
}
/**
 * The heart of one product. Markup contract (`snippets/wishlist-button.liquid`):
 *
 * - `data-product-id` and `data-product-handle` name the product. `data-variant-id` is the variant to store;
 *   with `data-follow-variant` the toggle keeps it equal to the variant the shopper selects in its section or dialog.
 * - `[data-wishlist-button]` is the button. The module writes `aria-pressed` on it, which alone carries the state:
 *   the name of a toggle stays the same ("Wishlist", then the product's title, so the names of the hearts on a page
 *   differ) and the announcement below says what a press did.
 * - `[data-wishlist-status]` is the polite region (empty in the markup so its first message is announced).
 *   A toggle inside an element with `data-wishlist-announces` stays silent, because that element announces the
 *   change itself: the wishlist page, whose card disappears with the heart that was pressed.
 *
 * Instance-owned: listeners and the announcement timer are released on disconnect, which is also the editor's unload.
 */
class WontioWishlistToggle extends Component {
    static observedAttributes = ['data-product-id'];
    #lifetime;
    #announceTimer = 0;
    /** Counts the variant selections seen, so only the newest one decides the stored variant. */
    #selection = 0;
    connectedCallback() {
        super.connectedCallback();
        this.#bind();
    }
    /** A morph keeps this element and resets its markup to what the server rendered, so the state is read again. */
    updatedCallback() {
        super.updatedCallback();
        this.#bind();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        window.clearTimeout(this.#announceTimer);
    }
    /** A morph can give this element another product without connecting it again. */
    attributeChangedCallback() {
        if (this.isConnected)
            this.#sync();
    }
    #bind() {
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        const { signal } = lifetime;
        this.addEventListener('click', this.#onClick, { signal });
        document.addEventListener(WishlistChangeEvent.eventName, this.#sync, { signal });
        if (this.hasAttribute('data-follow-variant')) {
            this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
        }
        this.#sync();
    }
    get #button() {
        return this.querySelector('[data-wishlist-button]');
    }
    #sync = () => {
        const { productId } = this.dataset;
        const saved = productId !== undefined && isSaved(productId);
        this.#button?.setAttribute('aria-pressed', String(saved));
        this.toggleAttribute('data-saved', saved);
    };
    /** The entry this toggle stands for, or undefined when its attributes cannot name a product. */
    #item() {
        return toItem({ id: this.dataset.productId, handle: this.dataset.productHandle, variantId: this.dataset.variantId });
    }
    #onClick = (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('[data-wishlist-button]'))
            return;
        const item = this.#item();
        if (!item) {
            this.#sync();
            return;
        }
        // The heart animates from now on (styles/wishlist.css); a heart drawn saved on load appears in place.
        this.toggleAttribute('data-motion', true);
        if (isSaved(item.id)) {
            removeFromWishlist(item.id);
            this.#announce('wishlist_removed');
            return;
        }
        this.#announce(addToWishlist(item) === 'full' ? 'wishlist_full' : 'wishlist_added');
    };
    #onProductSelect = (event) => {
        if (!isProductSelect(event) || event.product.id !== this.dataset.productId)
            return;
        const selection = ++this.#selection;
        event.promise
            .then(({ detail }) => {
            if (selection !== this.#selection || !this.isConnected)
                return;
            // A combination with no variant leaves nothing to preselect.
            this.dataset.variantId = detail?.resource?.id === undefined ? '' : String(detail.resource.id);
        })
            .catch(() => undefined);
    };
    /** Says the outcome once, unless an ancestor announces it. */
    #announce(key) {
        if (this.closest('[data-wishlist-announces]'))
            return;
        const region = this.querySelector('[data-wishlist-status]');
        const message = wishlistMessage(key);
        if (!region || message === '')
            return;
        // Clearing first lets the same outcome twice in a row be announced twice.
        region.textContent = '';
        window.requestAnimationFrame(() => {
            region.textContent = message;
        });
        window.clearTimeout(this.#announceTimer);
        this.#announceTimer = window.setTimeout(() => {
            region.textContent = '';
        }, ANNOUNCEMENT_MS);
    }
}
/** The class that cuts the icon under the bubble (`header-cart-icon-state`, styles/wontio.css, shared with the cart). */
const HAS_COUNT_CLASS = 'header-actions__cart-icon--has-cart';
/** From this many up the bubble shows as a dot, as the cart's does; the wishlist holds 50, so it is never reached. */
const BUBBLE_LIMIT = 100;
/**
 * The count of the wishlist on an icon: the header's wishlist link and the bottom bar's wishlist item. Markup
 * (`blocks/_header-wishlist.liquid`, `snippets/bottom-navigation-item.liquid`), the bubble being the cart's own from
 * `snippets/count-bubble.liquid`:
 *
 * - `[data-wishlist-bubble]` is the bubble and `[data-wishlist-bubble-count]` its number. The server renders both
 *   hidden and empty, because it cannot know the list; the bubble sits over the icon, so showing it moves nothing.
 * - `data-text-id` names an element outside the link that holds the description for assistive technology, "Saved
 *   products: 3", which stays empty for an empty list. It is outside the link so the link's name stays its label.
 *
 * The count follows `wishlist:change`. A product the shopper adds in this tab pulses the bubble, as the cart's does;
 * a change from another tab or a restored page does not. Like the other elements of the module it extends
 * `Component`, so a header render that keeps the element but resets its markup sets the count again.
 */
class WontioWishlistCount extends Component {
    #lifetime;
    /** The count the bubble shows, so only a rise caused by this tab pulses. */
    #shown = 0;
    connectedCallback() {
        super.connectedCallback();
        this.#bind();
    }
    updatedCallback() {
        super.updatedCallback();
        this.#bind();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
    }
    #bind() {
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        document.addEventListener(WishlistChangeEvent.eventName, this.#onChange, { signal: lifetime.signal });
        this.#render(false);
    }
    #onChange = (event) => {
        if (!(event instanceof WishlistChangeEvent))
            return;
        const { reason } = event.detail;
        this.#render(reason === 'add' || reason === 'merge');
    };
    #render(pulse) {
        const count = readWishlist().length;
        const bubble = this.querySelector('[data-wishlist-bubble]');
        const number = this.querySelector('[data-wishlist-bubble-count]');
        const rose = count > this.#shown;
        this.#shown = count;
        bubble?.classList.toggle('visually-hidden', count === 0);
        number?.classList.toggle('hidden', count === 0);
        if (number)
            number.textContent = count > 0 && count < BUBBLE_LIMIT ? String(count) : '';
        this.classList.toggle(HAS_COUNT_CLASS, count > 0);
        const { textId } = this.dataset;
        const description = textId ? document.getElementById(textId) : null;
        if (description)
            description.textContent = count > 0 ? `${wishlistMessage('wishlist_count')}: ${count}` : '';
        if (pulse && rose && bubble)
            void this.#pulse(bubble);
    }
    /** The bubble's own motion (`cart-bubble-motion`), started after the bubble is visible and removed when it ends. */
    async #pulse(bubble) {
        await new Promise((resolve) => {
            window.requestAnimationFrame(() => resolve());
        });
        bubble.classList.add('cart-bubble--animating');
        const text = bubble.querySelector('.cart-bubble__text');
        if (text)
            await onAnimationEnd(text);
        bubble.classList.remove('cart-bubble--animating');
    }
}
if (!customElements.get('wontio-wishlist-toggle')) {
    customElements.define('wontio-wishlist-toggle', WontioWishlistToggle);
}
if (!customElements.get('wontio-wishlist-count')) {
    customElements.define('wontio-wishlist-count', WontioWishlistCount);
}
